<script lang="ts">
    import Fa from 'svelte-fa'
    import { faEdit, faUser, faPrint, faPlus, faMinus, faFileImport } from '@fortawesome/free-solid-svg-icons'
    import Content from '../_common/content.svelte'
    import LoadSpinner from '../_common/load-spinner.svelte'
    import { onMount } from 'svelte'
    import type { User } from '../../modules/usersApi/users/user'
    import type { Person } from '../../modules/eventsApi/persons/person'
    import { PersonsRepository } from '../../modules/eventsApi/persons/persons.repository'
    import { PersonsService } from '../../modules/eventsApi/persons/persons.service'
    import dayjs from 'dayjs'
    import DateSelect from '../_common/date-select.svelte'
    import { CompaniesRepository } from '../../modules/usersApi/companies/companies.repository'
    import { CompaniesService } from '../../modules/usersApi/companies/companies.service'
    import Helper from '../_common/helper.svelte'
    import Popover from '../_common/popover.svelte'
    import FinancialForm from './financial-form.svelte'
    import FinancialImportOfx from './financial-import-ofx.svelte'
    import type { Account } from '../../modules/financialApi/accounts/account'
    import type { Entry } from '../../modules/financialApi/entries/entry'
    import { EntryTypeEnum } from '../../modules/financialApi/entries/entry.enum'

    let accounts: Account[] = []
    let account: Account = null

    let entries: Entry[] = []
    let filteredEntries: Entry[] = []

    const compR = new CompaniesRepository()
    const compS = new CompaniesService(compR)
    let users: User[] = []

    let personsR = new PersonsRepository()
    let personsS = new PersonsService(personsR)
    let persons: Person[] = []

    let show = false
    let dates: { from: Date; to: Date } = window.api.utils.dates.today()
    let dateSelect

    onMount(async () => {
        await loadAccounts()
        await loadEntities()
    })

    async function loadAccounts() {
        accounts = await window.api.financials.accounts.getAll()
    }

    let filterAccountId: string = null
    let filterType: EntryTypeEnum = null
    let filterStatus: number = null
    async function loadEntities() {
        try {
            show = true
            entries = []
            const filter = {
                from: dates?.from || undefined,
                to: dates?.to || undefined,
                accountId: account?.accountId || undefined,
            }

            // limita a navegação por mais de 7 dias
            if (!window.store.token.permissions.financial.entries.navigate) {
                const diff = dayjs(new Date()).diff(filter.from, 'days')
                if (diff > 7) {
                    dates.from = dayjs(new Date()).add(-7, 'day').toDate()
                    filter.from = dates.from
                    dateSelect.setDate(dates.from, dates.to)
                }
            }

            entries = await window.api.financials.entries.getAll(filter)
            persons = []
            let personsId = []
            for (const entry of entries) {
                personsId.push(entry.personId)
            }
            let promises = []
            for (let i = 0; i < personsId.length; i += 100) {
                const chunk = personsId.slice(i, i + 100)
                promises.push(personsS.getAll({ persons: chunk.join(',') }))
            }
            const results = await Promise.all(promises)
            for (const result of results) {
                persons = persons.concat(result)
            }
            for (let entry of entries) {
                entry.person = persons.find((el) => el.personId === entry.personId)
            }
            updateFilter()
        } catch (error) {
            evaluateError(error)
        } finally {
            show = false
        }
    }

    function evaluateError(error) {
        window.error(error)
    }

    function dateChanged(event) {
        dates = event.detail
        loadEntities()
    }

    function showPerson(personId) {
        window.person('show', personId)
    }

    let showForm = false
    let entryId: string = null
    let creditDebit: EntryTypeEnum = null
    function editEntity(id) {
        entryId = id
        showForm = true
    }
    function newEntity(_creditDebit: EntryTypeEnum) {
        entryId = null
        creditDebit = _creditDebit
        showForm = true
    }
    function formChanged() {
        loadEntities()
        showForm = false
        showImport = false
    }
    function formCanceled() {
        loadEntities() // como podem ocorrer lançamentos em sequencia e depois o usuário sair clicando em "fechar", sempre atualizar
        showForm = false
        showImport = false
    }

    function updateFilterStatus(status: number) {
        filterStatus = status
        updateFilter()
    }

    function updateFilterType(type: EntryTypeEnum) {
        filterType = type
        updateFilter()
    }

    function updateFilterAccountId(accountId: string) {
        filterAccountId = accountId
        updateFilter()
    }

    function updateFilter() {
        filteredEntries = entries.filter((el) => !filterStatus || el.status === filterStatus)
        filteredEntries = filteredEntries.filter((el) => !filterType || el.type === filterType)
        filteredEntries = filteredEntries.filter((el) => !filterAccountId || el.accountId === filterAccountId)
    }

    let showImport = false
    function ofxImport() {
        showImport = true
    }

    async function gotoNewFinan() {
        if (!confirm('Caso queira voltar para a versão anterior do financeiro vá até o menu FERRAMENTAS > CONFIGURAÇÕES GERAIS')) return
        await window.api.events.settings.updateConfigs({ financialVersionLatest: true })
        location.href = '/financial-4/home'
    }
</script>

{#if showForm}
    <Popover title="Lançamento" width="1100px" height="360px" on:close={formCanceled}>
        <FinancialForm {entryId} {creditDebit} on:change={formChanged} on:cancel={formCanceled} />
    </Popover>
{/if}

{#if showImport}
    <Popover title="Importação de Extrato" width="1100px" height="84vh" on:close={formChanged}>
        <FinancialImportOfx on:change={formChanged} on:cancel={formCanceled} />
    </Popover>
{/if}

<Content>
    <div class="grid-act">
        <DateSelect bind:this={dateSelect} startFrom={dates.from} startTo={dates.to} on:change={dateChanged} />
        <div class="spacer h-mobile" />
        <button class="btn btn-secondary h-mobile" on:click={() => window.print()}><Fa icon={faPrint} /></button>
        <button title="Importe seu extrato facilmente" class="btn btn-secondary h-mobile" on:click={ofxImport}><Fa icon={faFileImport} /></button>
        {#if window.store.token.permissions.pos.sales.write}
            <button class="btn btn-primary new" on:click={() => newEntity(EntryTypeEnum.credit)}>
                <Fa icon={faPlus} /> <span class="h-mobile"> Entrada</span>
            </button>
            <button class="btn btn-primary new" on:click={() => newEntity(EntryTypeEnum.debit)}>
                <Fa icon={faMinus} /> <span class="h-mobile"> Saída</span>
            </button>
        {/if}
    </div>
    <div class="alert m-3 alert-secondary text-center">
        Há uma nova versão do financeiro totalmente remodelada esperando você. Para atualizar <button on:click={gotoNewFinan} class="btn-btn-secondary"
            >clique aqui</button
        >
    </div>

    <div class="entries-outer">
        <div class="filter config-box h-print">
            <div class="text-muted">Filtros</div>
            <div class="filter-items">
                <button class:selected={filterStatus === null} on:click={() => updateFilterStatus(null)} class="btn btn-sm btn-link d-block"
                    >Pagas e em aberto</button
                >
                <button class:selected={filterStatus === 100} on:click={() => updateFilterStatus(100)} class="btn btn-sm btn-link d-block">Apenas pagas</button>
                <button class:selected={filterStatus === 95} on:click={() => updateFilterStatus(95)} class="btn btn-sm btn-link d-block"
                    >Apenas em aberto</button
                >
            </div>
            <div class="filter-items mt-4">
                <button class:selected={filterType === null} on:click={() => updateFilterType(null)} class="btn btn-sm btn-link d-block"
                    >Entradas e saídas</button
                >
                <button
                    class:selected={filterType === EntryTypeEnum.credit}
                    on:click={() => updateFilterType(EntryTypeEnum.credit)}
                    class="btn btn-sm btn-link d-block">Apenas entradas</button
                >
                <button
                    class:selected={filterType === EntryTypeEnum.debit}
                    on:click={() => updateFilterType(EntryTypeEnum.debit)}
                    class="btn btn-sm btn-link d-block">Apenas saídas</button
                >
            </div>
            <div class="filter-items mt-4">
                <button class:selected={filterAccountId === null} on:click={() => updateFilterAccountId(null)} class="btn btn-sm btn-link d-block"
                    >Todas as contas</button
                >
                {#each accounts as account}
                    <button
                        class:selected={filterAccountId === account.accountId}
                        on:click={() => updateFilterAccountId(account.accountId)}
                        class="btn btn-sm btn-link d-block nowrap">{account.label}</button
                    >
                {/each}
            </div>
        </div>
        <div class="entries">
            {#if filteredEntries.length > 0}
                <div class="subtitle mt-1 mb-1 h-mobile h-full">Lançamentos Financeiros</div>
                <div class="grid-head px-2">
                    <div class="row">
                        <div class="col-1 h-mobile h-full h-print">Origem</div>
                        <div class="col-1">Status</div>
                        <div class="col-1">Data</div>
                        <div class="col">&nbsp;Empresa / Pessoa</div>
                        <div class="col">Categoria</div>
                        <div class="col">Descrição</div>
                        <div class="col-2 text-end">Valor</div>
                        <div class="col-2 col-md-2 h-print" />
                    </div>
                </div>
            {/if}
            <div class="grid-body mb-4 px-2">
                {#if !show}
                    {#if filteredEntries.length > 0}
                        {#each filteredEntries as entry}
                            <div class="row line liner py-1 type_{entry.type}">
                                <div class="col-1 h-mobile h-full h-print">
                                    <div class="badge status_{entry.source}">
                                        {entry.source === 'import' ? 'Importação' : 'Digitação'}
                                    </div>
                                </div>
                                <div class="col-1">
                                    <div class="badge status_{entry.status}">
                                        {entry.status === 100 ? 'Pago' : 'Em aberto'}
                                    </div>
                                </div>
                                <div class="col-1">{dayjs(entry.paymentAt).format('DD/MM/YY')}</div>
                                <div class="col nowrap">&nbsp;{entry.person?.name || '-'}</div>
                                <div class="col nowrap">{entry.category?.label}</div>
                                <div class="col nowrap">{entry.description}</div>
                                <div class="col-2 text-end">
                                    {(entry.type === EntryTypeEnum.debit ? '-' : '') + entry.value.toFixed(2)}
                                </div>
                                <div class="col-2 col-md-2 text-md-end h-print">
                                    {#if window.store.token.permissions.persons.show && entry.personId}
                                        <button
                                            class="btn btn-secondary btn-sm h-mobile"
                                            on:mousedown|stopPropagation
                                            on:click|stopPropagation={() => showPerson(entry.personId)}
                                        >
                                            <Fa size="sm" icon={faUser} /></button
                                        >
                                    {/if}
                                    <button
                                        class="btn btn-secondary btn-sm"
                                        on:click={() => {
                                            editEntity(entry.entryId)
                                        }}
                                    >
                                        <Fa icon={faEdit} /> <span class="h-mobile">Editar</span>
                                    </button>
                                </div>
                            </div>
                        {/each}
                        <div class="row">
                            <div class="col text-end">
                                Total {filteredEntries.reduce((acc, el) => (acc += el.type === EntryTypeEnum.credit ? el.value : -el.value), 0).toFixed(2)}
                            </div>
                            <div class="col-2 col-md-2" />
                        </div>
                    {:else}
                        <div class="text-center text-muted mt-3">Nenhum dado a ser exibido</div>
                    {/if}
                {/if}
            </div>
        </div>
    </div>
    <LoadSpinner {show} />
</Content>

<style>
    .resume {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
        font-size: 13px;
    }
    .btns {
        text-align: right;
    }
    .entries-outer {
        display: flex;
    }
    .entries-outer .filter {
        flex: 0 0 190px;
        margin: 12px 16px 16px 0px;
    }
    .entries-outer .filter .btn-link {
        text-decoration: none;
    }
    .entries-outer .filter .selected {
        text-decoration: underline !important;
        color: rgb(var(--finan-color)) !important;
    }
    .entries-outer .entries {
        flex: 1 1 auto;
    }

    @media (max-width: 900px) {
        .grid-act {
            background-color: unset;
            display: flex;
            justify-content: space-around;
        }
        .grid-act .btn {
            max-width: 120px;
            flex-grow: 1;
        }
    }
    @media (min-width: 1920px) {
    }

    @media print {
    }
</style>
